// Vendors
import { useEffect, useRef, useState } from 'react';
// Contexts
import { useFetchErrorContextConsumerHook } from 'contexts/fetch-error.context';
// Hooks
import { useWebsocketHook } from 'hooks/websocket.hook';
// Event Handlers
import { IframeSocketHandlers } from '../handlers/iframe-socket.handlers';
import { IframeHandlers } from '../handlers/iframe.handlers';
// Types
import { IframeComponentType } from 'components/iframe/types/iframe.component.type';
import { IframeHookType } from './types/iframe.hook.type';

const useIframeHook = ({
  onReceiveDataHandler: onReceiveData,
  listenWsEvents,
  onEventListen,
  resolver,
  shouldConnectWs,
  ...restProps
}: Omit<IframeComponentType, 'frameGap'>): IframeHookType => {
  const [frameLoaded, setFrameLoaded] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(false);
  const [src, setSrc] = useState<string>('');
  const timeoutRef = useRef<number>(0);

  const { setError } = useFetchErrorContextConsumerHook();

  const {
    handleAddIframeEventListener,
    handleFetchFrameUrlFromAPIEvent,
    handleCatchFrameComponentLoadErrorEvent,
    handleCleanIframeEventListener,
    ...restFrameHandlers
  } = IframeHandlers({
    onEventListen,
    resolver,
    setFrameLoaded,
    setFetching,
    setError,
    setSrc,
  });

  const {
    handleConnectWebSocketChannelEvent,
    handleDisconnectWebSocketFromChannelEvent,
    handleSendWebSocketMessageEvent,
    websocketConnected,
  } = useWebsocketHook();

  const {
    handleStartWhenSockedAndFrameLoadedEvent,
    handleSendMessageWhenWebSockedAndFrameLoadedEvent,
  } = IframeSocketHandlers({
    ...restProps,
    frameLoaded,
    fetching,
    handleConnectWebSocketChannelEvent,
    handleDisconnectWebSocketFromChannelEvent,
    handleSendWebSocketMessageEvent,
    websocketConnected,
    onReceiveData,
    timeoutRef,
    setError,
    onError: handleCatchFrameComponentLoadErrorEvent,
  });

  useEffect(() => {
    handleFetchFrameUrlFromAPIEvent();
  }, []);

  useEffect(() => {
    let finalizer;
    if (shouldConnectWs) {
      finalizer = handleStartWhenSockedAndFrameLoadedEvent();
      listenWsEvents && handleAddIframeEventListener();
    } else {
      handleAddIframeEventListener();
    }
    return () => {
      finalizer?.();
      handleCleanIframeEventListener();
    };
  }, [frameLoaded, fetching]);

  useEffect(() => {
    shouldConnectWs && handleSendMessageWhenWebSockedAndFrameLoadedEvent();
  }, [frameLoaded, websocketConnected]);

  return {
    ...restFrameHandlers,
    handleCatchFrameComponentLoadErrorEvent,
    src,
  };
};

export { useIframeHook };
